import React, { useContext, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import SearchBar from '@components/shared/Search/SearchBar/SearchBar';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import { hashCode } from '@lib/string';
import { useTheme } from '@components/theme';
import { SHOP_SEARCH_URL } from '@components/pages/ShopV3/util/search';
import DropDownMenu from '@components/shared/shop/ShopNavigationMenu/DropDownMenu/DropDownMenu';
import useShopMenu from '@components/shared/shop/ShopNavigationMenu/UseShopMenu';
import TrackingEvents from '@constants/tracking/events';
import { userIsDesignerOrAdmin } from '@lib/auth/auth';
import UserContext from '@context/User/user-context';
import { navigationHeights } from '../../../../styles/variables';
import { IShopNavigationMenu, NAVIGATION_SLUGS } from './model';
import styles from './ShopNavigationMenu.module.scss';
import ShopNavigationHoverContext from './ShopNavigationContext';

export const SHOP_MENU_ANIMATION_TIMEOUT = 250;

interface IShopNavigationMenuProps {
  shopNavigationMenu: IShopNavigationMenu[];
  unbounceBannerHeight?: number;
  isFixed?: boolean;
}

export default function ShopNavigationMenu({
  shopNavigationMenu,
  unbounceBannerHeight = 0,
  isFixed = false
}: IShopNavigationMenuProps) {
  const theme = useTheme();
  const router = useRouter();
  const { user } = useContext(UserContext);
  const isAdminOrDesigner = userIsDesignerOrAdmin(user);
  const [searchValue, setSearchValue] = useState('');
  const { setShopNavigationHovered } = useContext(ShopNavigationHoverContext);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    activeDropdownMenu,
    animateDropdownContent,
    closeNav,
    isActiveOption,
    isNavOpen,
    navRef,
    openNav,
    setActiveDropdownMenu,
    setAnimateDropdownContent,
    setIsNavOpen
  } = useShopMenu();

  const handleSearchClick = () => {
    analyticsTrack('shop_nav_search_opened', {
      title: `Shop Navigation Menu | Search`
    });
  };

  const handleEnter = (menu: IShopNavigationMenu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!menu.option?.is_dropdown_enabled) {
      timeoutRef.current = setTimeout(closeNav, SHOP_MENU_ANIMATION_TIMEOUT);
      return;
    }

    timeoutRef.current = setTimeout(() => openNav(menu), SHOP_MENU_ANIMATION_TIMEOUT);
  };

  const handleLeave = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    const dropDown = document.querySelector('#shop-dropdown');

    if (dropDown && event && dropDown.contains(event.relatedTarget as Node)) {
      return;
    }

    setAnimateDropdownContent(false);
    timeoutRef.current = setTimeout(() => {
      setAnimateDropdownContent(true);
      setActiveDropdownMenu(null);
      setIsNavOpen(false);
    }, SHOP_MENU_ANIMATION_TIMEOUT);
  };

  return (
    <>
      <div
        className={classNames(
          theme.styles.Body,
          styles.ShopNavigationMenuWrapper,
          { [styles.Fixed]: isFixed },
          { [styles.Sticky]: !isFixed }
        )}
        style={{
          top: `${unbounceBannerHeight}px`,
          // eslint-disable-next-line no-nested-ternary
          marginTop: isFixed ? (isAdminOrDesigner ? `${navigationHeights.navHeight}px` : `${navigationHeights.mainNavHeight}px`) : 0
        }}
      >
        <nav
          className={styles.ShopNavigationMenu}
          onMouseEnter={() => setShopNavigationHovered(true)}
          onMouseLeave={() => {
            setShopNavigationHovered(false);
            setIsNavOpen(false);
          }}
          ref={navRef}
        >
          <ul>
            {shopNavigationMenu.map((shopMenuItem) => {
              const { item, url } = shopMenuItem;
              return (
                <li
                  key={hashCode(item.title)}
                  className={styles.MenuItem}
                  onMouseEnter={() => handleEnter(shopMenuItem)}
                  onMouseLeave={handleLeave}
                >
                  <a
                    className={`${styles.NavbarOption} ${
                      isActiveOption(item) ? styles.ActiveOption : ''
                    }`}
                    onClick={(e) => {
                      if (item.slug === NAVIGATION_SLUGS.FEATURE) {
                        e.preventDefault();
                      } else {
                        analyticsTrack('shop_nav_clicked', {
                          title: `Shop Navigation Menu | ${item.title}`,
                          url
                        });
                      }
                    }}
                    href={url}
                  >
                    <span>{item.title}</span>
                  </a>
                </li>
              );
            })}
          </ul>

          <DropDownMenu
            shopNavigationMenu={activeDropdownMenu}
            animateDropdownContent={animateDropdownContent}
            isNavOpen={isNavOpen}
            setAnimateDropdownContent={setAnimateDropdownContent}
            timeoutRef={timeoutRef}
            setActiveDropdownMenu={setActiveDropdownMenu}
            unbounceBannerHeight={unbounceBannerHeight}
          />
        </nav>
        <div className={styles.SearchBarContainer}>
          <SearchBar
            className={styles.SearchBar}
            text={searchValue}
            onChange={(value) => setSearchValue(value)}
            onClose={handleSearchClick}
            onSubmit={async (e) => {
              e.preventDefault();
              const text = searchValue?.trim();

              analyticsTrack(TrackingEvents.SHOP_NAV_SEARCHED, {
                title: `Shop Navigation Menu | Search`,
                searchedText: text
              });

              await router.push({
                pathname: SHOP_SEARCH_URL,
                query: { text }
              });
            }}
          />
        </div>
      </div>

      <div className={`${styles.ShadowLayer} ${isNavOpen ? styles.Entering : styles.Leaving}`} />
    </>
  );
}
